import { askConfirm } from '../components/ask';
import { phoneField } from '../phone';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, get, post, put } from '../api';
import { useBrand } from '../components/Brand';
import { HistoryTable } from '../components/HistoryTable';
import { TagPicker } from '../components/TagPicker';
import { contractorTypeOptions } from '../contractorTypes';
import { useLoad } from '../hooks';
import { UnsavedChanges } from '../components/UnsavedChanges';

interface Form {
  kind: 'person' | 'organisation';
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  website: string;
  tax_reference: string;
  notes: string;
  address: {
    line1: string;
    line2: string;
    locality: string;
    region: string;
    postcode: string;
    country: string;
  };
  roles: string[];
  customer: { access_notes: string | null };
  client: {
    tax_code: 'gst' | 'gst_free';
    reference_label: string;
    reference_required: boolean;
    invoice_email: string;
    billing_cycle: string;
    billing_anchor_date: string;
  };
  contractor: { contractor_type: string; gst_applicable: boolean };
  tag_ids: number[];
}

const BLANK: Form = {
  kind: 'person',
  name: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  mobile: '',
  website: '',
  tax_reference: '',
  notes: '',
  address: { line1: '', line2: '', locality: '', region: '', postcode: '', country: 'AU' },
  roles: [],
  customer: { access_notes: null },
  client: {
    tax_code: 'gst',
    reference_label: '',
    reference_required: false,
    invoice_email: '',
    billing_cycle: '',
    billing_anchor_date: '',
  },
  contractor: { contractor_type: '', gst_applicable: false },
  tag_ids: [],
};

type Loaded = Partial<Omit<Form, 'address' | 'customer' | 'client' | 'contractor' | 'notes'>> & {
  state: string;
  display_name?: string;
  notes: string | null;
  address: Form['address'] | null;
  customer: Form['customer'] | null;
  client:
    (Omit<Form['client'], 'billing_anchor_date'> & { billing_anchor_date: string | null }) | null;
  contractor: Form['contractor'] | null;
};

/** Server shape to form state; profiles the person doesn't hold keep their blank defaults. */
function toForm(c: Loaded): Form {
  return {
    ...BLANK,
    kind: c.kind ?? 'person',
    name: c.name ?? '',
    first_name: c.first_name ?? '',
    last_name: c.last_name ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    mobile: c.mobile ?? '',
    website: c.website ?? '',
    tax_reference: c.tax_reference ?? '',
    notes: c.notes ?? '',
    address: c.address ?? BLANK.address,
    roles: c.roles ?? [],
    customer: c.customer ?? BLANK.customer,
    client: c.client
      ? { ...c.client, billing_anchor_date: c.client.billing_anchor_date ?? '' }
      : BLANK.client,
    contractor: c.contractor ?? BLANK.contractor,
    tag_ids: c.tag_ids ?? [],
  };
}

/** Form state to the save body: only held roles send a profile, blank date becomes null. */
export function toBody(f: Form) {
  const hasAddress = Object.values(f.address).some((v) => v.trim() !== '' && v !== 'AU');
  return {
    kind: f.kind,
    name: f.name,
    first_name: f.first_name,
    last_name: f.last_name,
    email: f.email,
    phone: f.phone,
    mobile: f.mobile,
    website: f.website,
    tax_reference: f.tax_reference,
    notes: f.notes === '' ? null : f.notes,
    address: hasAddress ? f.address : null,
    roles: f.roles,
    customer: f.roles.includes('customer') ? f.customer : undefined,
    client: f.roles.includes('client')
      ? { ...f.client, billing_anchor_date: f.client.billing_anchor_date || null }
      : undefined,
    contractor: f.roles.includes('contractor') ? f.contractor : undefined,
    tag_ids: f.tag_ids,
  };
}

const ROLE_LABELS: [string, string][] = [
  ['customer', 'Customer'],
  ['client', 'Client (pays invoices)'],
  ['contractor', 'Contractor'],
  ['referrer', 'Referrer'],
];

function Field(props: { label: string; error?: string; children: ReactNode }) {
  // The message sits beside the label, not inside it, so the input's name stays just the label.
  return (
    <div className={props.error ? 'field invalid' : 'field'}>
      <label>
        {props.label}
        {props.children}
      </label>
      {props.error && <span className="error">{props.error}</span>}
    </div>
  );
}

interface Related {
  id: number;
  customer_name: string;
  client_name: string;
  assignment_status: string;
  charge_rate: string;
  charge_rate_unit: string;
}

/** The work this person is part of, so a record reads as more than a form. */
function RelatedWork(props: { id: string; roles: string[] }) {
  const list = useLoad(
    () => get<{ items: Related[] }>(`/api/assignments?contact_id=${props.id}&limit=20`),
    [props.id],
  );
  const items = list.data?.items ?? [];
  return (
    <section className="panel related" aria-labelledby="related-h">
      <h2 id="related-h">Assignments</h2>
      {list.data && items.length === 0 && (
        <p className="empty">
          No assignments yet. <Link to="/assignments/new">Add an assignment</Link>
        </p>
      )}
      <ul className="plain">
        {items.map((a) => (
          <li key={a.id}>
            <Link to={`/assignments/${a.id}`}>{a.customer_name}</Link>
            <span className="muted">
              {a.client_name ? ` · for ${a.client_name}` : ''} · {a.assignment_status} · $
              {Number(a.charge_rate).toFixed(2)} / {a.charge_rate_unit}
            </span>
          </li>
        ))}
      </ul>
      {props.roles.includes('contractor') && (
        <p>
          <Link to={`/schedule?who=${props.id}`}>See their week on the schedule</Link>
        </p>
      )}
    </section>
  );
}

export function PersonForm() {
  const { id } = useParams();
  const existing = useLoad(
    () => (id ? get<Loaded>(`/api/contacts/${id}`) : Promise.resolve(null)),
    [id],
  );
  if (id && existing.loading) return <p>Loading…</p>;
  if (id && existing.error) {
    return (
      <p role="alert" className="error">
        {existing.error}
      </p>
    );
  }
  // Keyed so navigating between people resets the form state.
  return (
    <>
      <Editor
        key={id ?? 'new'}
        id={id}
        title={existing.data?.display_name}
        initial={existing.data ? toForm(existing.data) : BLANK}
        archived={existing.data?.state === 'inactive'}
      />
      {id && <RelatedWork id={id} roles={existing.data?.roles ?? []} />}
      {id && <HistoryTable table="contacts" id={id} />}
    </>
  );
}

function Editor(props: { id?: string; title?: string; initial: Form; archived: boolean }) {
  const nav = useNavigate();
  const [f, setF] = useState<Form>(props.initial);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const brand = useBrand();
  const typeOptions = contractorTypeOptions(brand?.slug ?? '');

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((s) => ({ ...s, [k]: v }));
  const text = (
    k:
      | 'name'
      | 'first_name'
      | 'last_name'
      | 'email'
      | 'phone'
      | 'mobile'
      | 'website'
      | 'tax_reference',
  ) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) =>
      set(k, k === 'phone' || k === 'mobile' ? phoneField(e.target.value) : e.target.value),
  });
  const held = (r: string) => f.roles.includes(r);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setProblems([]);
    setFields({});
    try {
      const body = toBody(f);
      // Left on its default (a brand-new contractor never touched the picker): this
      // business's own first option, rather than a value from another business's list.
      if (body.contractor && !body.contractor.contractor_type) {
        body.contractor.contractor_type = typeOptions[0]?.value ?? '';
      }
      if (props.id) await put(`/api/contacts/${props.id}`, body);
      else await post('/api/contacts', body);
      nav('/people');
    } catch (err) {
      if (err instanceof ApiError) {
        setProblems(err.problems.length ? err.problems : [err.message]);
        setFields(err.fields);
      } else setProblems(['Could not reach the server']);
      setBusy(false);
    }
  }

  async function archive() {
    if (!props.id) return;
    const yes = await askConfirm({
      title: 'Deactivate this person?',
      body: 'They can be restored later.',
      confirmLabel: 'Deactivate',
      danger: true,
    });
    if (!yes) return;
    try {
      await post(`/api/contacts/${props.id}/archive`);
      nav('/people');
    } catch (err) {
      setProblems([err instanceof ApiError ? err.message : 'Could not reach the server']);
    }
  }

  const dirty = !busy && JSON.stringify(f) !== JSON.stringify(props.initial);

  return (
    <form onSubmit={submit} className="stack" noValidate>
      <UnsavedChanges when={dirty} />
      <div className="row start">
        <h1>{props.id ? props.title || 'Person' : 'Add person'}</h1>
        {props.id &&
          f.roles.map((r) => (
            <span key={r} className="pill info">
              {ROLE_LABELS.find(([k]) => k === r)?.[1].replace(' (pays invoices)', '') ?? r}
            </span>
          ))}
      </div>
      {props.archived && <p className="notice">This person is deactivated.</p>}
      {problems.length > 0 && (
        <div role="alert" className="error box">
          <strong>Not saved.</strong>
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      <fieldset>
        <legend>Who</legend>
        <Field label="Type">
          <select value={f.kind} onChange={(e) => set('kind', e.target.value as Form['kind'])}>
            <option value="person">Person</option>
            <option value="organisation">Organisation</option>
          </select>
        </Field>
        {f.kind === 'organisation' ? (
          <Field label="Organisation name" error={fields.display_name}>
            <input {...text('name')} />
          </Field>
        ) : (
          <>
            <Field label="First name">
              <input {...text('first_name')} />
            </Field>
            <Field label="Last name" error={fields.last_name}>
              <input {...text('last_name')} />
            </Field>
          </>
        )}
        <Field label="Email" error={fields.email}>
          <input type="email" {...text('email')} />
        </Field>
        <Field label="Mobile">
          <input type="tel" {...text('mobile')} />
        </Field>
        <Field label="Phone">
          <input type="tel" {...text('phone')} />
        </Field>
      </fieldset>

      <fieldset>
        <legend>Roles</legend>
        {fields.roles && <span className="error">{fields.roles}</span>}
        {ROLE_LABELS.map(([role, label]) => (
          <label key={role} className="check">
            <input
              type="checkbox"
              checked={held(role)}
              onChange={(e) =>
                set(
                  'roles',
                  e.target.checked ? [...f.roles, role] : f.roles.filter((r) => r !== role),
                )
              }
            />
            {label}
          </label>
        ))}
      </fieldset>

      {held('customer') && (
        <fieldset>
          <legend>Customer</legend>
          <Field label="Access notes (leave blank to use the site's default)">
            <textarea
              value={f.customer.access_notes ?? ''}
              onChange={(e) =>
                set('customer', { access_notes: e.target.value === '' ? null : e.target.value })
              }
            />
          </Field>
        </fieldset>
      )}

      {held('client') && (
        <fieldset>
          <legend>Client billing</legend>
          <Field label="Tax">
            <select
              value={f.client.tax_code}
              onChange={(e) =>
                set('client', { ...f.client, tax_code: e.target.value as 'gst' | 'gst_free' })
              }
            >
              <option value="gst">GST applies</option>
              <option value="gst_free">GST-free</option>
            </select>
          </Field>
          <Field label="Invoice email" error={fields.client_invoice_email}>
            <input
              type="email"
              value={f.client.invoice_email}
              onChange={(e) => set('client', { ...f.client, invoice_email: e.target.value })}
            />
          </Field>
          <Field label="Reference label (e.g. Plan number)" error={fields.client_reference_label}>
            <input
              value={f.client.reference_label}
              onChange={(e) => set('client', { ...f.client, reference_label: e.target.value })}
            />
          </Field>
          <label className="check">
            <input
              type="checkbox"
              checked={f.client.reference_required}
              onChange={(e) => set('client', { ...f.client, reference_required: e.target.checked })}
            />
            A reference is required on invoices
          </label>
          <Field label="Billing cycle" error={fields.client_billing_cycle}>
            <select
              value={f.client.billing_cycle}
              onChange={(e) => set('client', { ...f.client, billing_cycle: e.target.value })}
            >
              <option value="">Invoice as needed</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="four_weekly">Every 4 weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </Field>
          {f.client.billing_cycle && (
            <Field label="Cycle starts on" error={fields.client_billing_anchor_date}>
              <input
                type="date"
                value={f.client.billing_anchor_date}
                onChange={(e) =>
                  set('client', { ...f.client, billing_anchor_date: e.target.value })
                }
              />
            </Field>
          )}
        </fieldset>
      )}

      {held('contractor') && (
        <fieldset>
          <legend>Contractor details</legend>
          <Field label="Type of work">
            <select
              value={f.contractor.contractor_type || (typeOptions[0]?.value ?? '')}
              onChange={(e) =>
                set('contractor', { ...f.contractor, contractor_type: e.target.value })
              }
            >
              {/* A value from before this business's own list looked like this: kept, not
                  silently swapped out, so saving the rest of the form doesn't lose it. */}
              {f.contractor.contractor_type &&
                !typeOptions.some((o) => o.value === f.contractor.contractor_type) && (
                  <option value={f.contractor.contractor_type}>
                    {f.contractor.contractor_type}
                  </option>
                )}
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <label className="check">
            <input
              type="checkbox"
              checked={f.contractor.gst_applicable}
              onChange={(e) =>
                set('contractor', { ...f.contractor, gst_applicable: e.target.checked })
              }
            />
            Registered for GST
          </label>
        </fieldset>
      )}

      <fieldset>
        <legend>Address</legend>
        {(['line1', 'line2', 'locality', 'region', 'postcode'] as const).map((k) => (
          <Field
            key={k}
            label={
              {
                line1: 'Address',
                line2: 'Address line 2',
                locality: 'Suburb',
                region: 'State',
                postcode: 'Postcode',
              }[k]
            }
          >
            <input
              value={f.address[k]}
              onChange={(e) => set('address', { ...f.address, [k]: e.target.value })}
            />
          </Field>
        ))}
        <Field label="Country code" error={fields.address_country}>
          <input
            value={f.address.country}
            onChange={(e) => set('address', { ...f.address, country: e.target.value })}
          />
        </Field>
      </fieldset>

      <Field label="Office notes">
        <textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>

      <fieldset>
        <legend>Tags</legend>
        <TagPicker label="Tags" value={f.tag_ids} onChange={(v) => set('tag_ids', v)} />
      </fieldset>

      <div className="row">
        <button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="quiet" onClick={() => nav('/people')}>
          Cancel
        </button>
        {props.id && !props.archived && (
          <button type="button" className="danger" onClick={() => void archive()}>
            Deactivate
          </button>
        )}
      </div>
    </form>
  );
}
